import "containers/BudgetHome/Irrigation/IrrigatorMap.css";
import "components/Map/map.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useOnline } from "common/hooks";
import * as utils from "common/utils";
import * as geoJsonUtils from "common/geoJsonUtils";
import { useIsReadOnlyMode } from "components/AnalysisRenderMode";
import Spinner from "components/Spinner/Spinner";
import { useGoogleApi, useGoogleMap } from "components/Map/hooks";
import { Panel, PanelBody } from "components/Panel";
import MapToolbar from "components/Map/MapToolbar";
import MapZoomButton from "components/Map/MapZoomButton";
import MapErrorLabels from "components/Map/MapErrorLabels";
import { BlockLabelsLayerToggle } from "components/Map";
import TestGeoJsonLoader from "components/Map/TestGeoJsonLoader";
import Button from "components/Button/Button";

const MIN_DRAWN_AREA = 0.1;
const MIN_BLOCK_INTERSECTION_PERCENT = 5;
const MAX_IRRIGATORS_PER_BLOCK = 2;

export default function IrrigatorMap({ farm, analysis, irrigator, onChange }) {
    const isReadOnlyMode = useIsReadOnlyMode();
    const online = useOnline();
    const { map, mapId, setMapRef } = useGoogleMap({ center: { lat: farm.latitude, lng: farm.longitude }, zoom: 12 });
    const [geoJson, syncMapDataToGeoJson] = useAnalysisGeoJson(analysis);
    const [toggleDrawing, setToggleDrawing] = useState(false);
    const [drawingMode, setDrawingMode] = useState(irrigator.isNew ? "polygon" : null);

    const validateFeature = useCallback(
        (mapData, featureId) => {
            if (!featureId) return;

            mapData.toGeoJson((mapGeoJson) => {
                let error;

                // Run the rules that validate the feature that triggered this validation...
                const triggerFeature = mapGeoJson.features.find((f) => f.id === featureId);

                // RULE 1 - feature that triggered this validation must not overlap itself.
                if (triggerFeature) {
                    error = geoJsonUtils.selfIntersects(triggerFeature) ? "Must not overlap itself" : undefined;
                }

                // RULE 2 - feature that triggered this validation must not overlap other irrigator features.
                if (!error) {
                    const featuresForOtherIrrigators = mapGeoJson.features.filter((f) => f.properties.irrigatorId && f.properties.irrigatorId !== irrigator.id);
                    featuresForOtherIrrigators.forEach((featureForOtherIrrigator) => {
                        if (geoJsonUtils.intersects(triggerFeature, featureForOtherIrrigator)) error = "Must not overlap other drawn irrigated areas";
                    });
                }

                // RULE 3 - feature that triggered this validation must be at least a minimum size.
                if (!error) {
                    const drawnArea = geoJsonUtils.area(triggerFeature);
                    if (drawnArea < MIN_DRAWN_AREA) error = "Must be greater than 0.1 ha";
                }

                // Run the rules that validate all the features of the current irrigator...
                const allFeaturesForThisIrrigtor = mapGeoJson.features.filter((f) => f.properties.irrigatorId === irrigator.id);
                const unionedIrrigatorFeature = geoJsonUtils.union(allFeaturesForThisIrrigtor);

                if (!error) {
                    let intersectsWithAProductiveBlock = false;
                    analysis.blocks
                        .filter((b) => b.isProductive && b.type !== "ProductiveOutdoorPigs")
                        .forEach((block) => {
                            if (!error) {
                                const blockFeatures = mapGeoJson.features.filter((f) => f.properties.blockId === block.id);
                                if (blockFeatures.length > 0) {
                                    const unionedBlockFeature = geoJsonUtils.union(blockFeatures);
                                    const intersection = geoJsonUtils.intersection(unionedIrrigatorFeature, unionedBlockFeature);
                                    if (intersection) {
                                        intersectsWithAProductiveBlock = true;

                                        // RULE 4 - block must not have more than the max number of irrigators.
                                        const numberOfOtherIrrigatorsAlreadyOnThisBlock = (analysis.irrigators || [])
                                            .filter((irr) => irr.id !== irrigator.id)
                                            .reduce((count, irr) => {
                                                if (irr.applications.some((app) => app.blockIds.includes(block.id))) count++;
                                                return count;
                                            }, 0);
                                        if (numberOfOtherIrrigatorsAlreadyOnThisBlock >= MAX_IRRIGATORS_PER_BLOCK) {
                                            error = `The "${block.name}" block is already irrigated by the maximum number of irrigation systems`;
                                        }

                                        if (!error) {
                                            const drawnBlockArea = geoJsonUtils.area(unionedBlockFeature);
                                            const drawnIrrigatedArea = utils.round((intersection.area / drawnBlockArea) * block.areaInHectares, 1);
                                            const drawnIrrigatedPercentage = utils.round((drawnIrrigatedArea / block.areaInHectares) * 100, 1);

                                            // RULE 5 - drawn irrigator must intersect by a minimim percentage of any block it intersects.
                                            if (drawnIrrigatedPercentage < MIN_BLOCK_INTERSECTION_PERCENT) {
                                                error = "Must intersect at least 5% of any block it intersects";
                                            }

                                            // RULE 6 - a drawn irrigator must not cover so much of the block that any non-drawn irrigator that is also on
                                            //          the block no longer has at least 5% over coverage.
                                            if (!error) {
                                                const nonDrawnArea = (analysis.irrigators || []).reduce((result, irr) => {
                                                    if (!result) {
                                                        result = (irr.nonDrawnArea || []).find((nda) => nda.blockId === block.id);
                                                    }
                                                    return result;
                                                }, null);
                                                if (nonDrawnArea) {
                                                    const unIrrigatedArea = block.areaInHectares - drawnIrrigatedArea;
                                                    const nonDrawnIrrigatedArea = utils.round((unIrrigatedArea * nonDrawnArea.percentageOfNonDrawnArea) / 100, 1);
                                                    const nonDrawnIrrigatedPercentage = utils.round((nonDrawnIrrigatedArea / block.areaInHectares) * 100, 1);
                                                    if (nonDrawnIrrigatedPercentage < 5) {
                                                        error = `The "${block.name}" block has too much coverage.`;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        });

                    // RULE 7 - irrigator must intersect at least one productive block.
                    if (!error && !intersectsWithAProductiveBlock) {
                        error = "Must intersect at least one productive block";
                    }
                }

                mapData.getFeatureById(featureId).setProperty("error", error);
                syncMapDataToGeoJson(mapData);
            });
        },
        [analysis.blocks, analysis.irrigators, irrigator.id, syncMapDataToGeoJson]
    );

    const mapData = useMapData(map, geoJson);
    useMapStyle(mapData, irrigator.id);
    useCustomDrawing(map, mapData, irrigator, validateFeature, drawingMode, setDrawingMode);
    useValidateOnLoad(mapData, irrigator.id, validateFeature);
    useOnPolygonHover(mapData, irrigator.id, drawingMode);
    useOnPolygonClick(mapData, irrigator.id, drawingMode);
    useOnPolygonAdd(mapData, validateFeature);
    useOnPolygonEdit(mapData, validateFeature);

    const mapError = !online && "Overseer is currently offline. Mapping is unavailable.";
    const mapLoading = online && !geoJson && !mapError;
    const isIrrigatorDrawn = geoJson && geoJson.features.some((f) => f.properties.irrigatorId === irrigator.id);
    const hasDrawnProductiveBlocks =
        geoJson &&
        geoJson.features
            .filter((f) => f.properties.blockId)
            .some((f) => {
                const block = analysis.blocks.find((b) => b.id === f.properties.blockId);
                if (!block) return false;
                return block.isProductive && block.type !== "ProductiveOutdoorPigs";
            });
    const showMap = !mapLoading && !mapError && (isIrrigatorDrawn || toggleDrawing);

    const deleteSelectedPolygons = useCallback(() => {
        mapData.forEach((feature) => {
            if (feature.getProperty("selected")) mapData.remove(feature);
        });
        syncMapDataToGeoJson(mapData);
    }, [mapData, syncMapDataToGeoJson]);

    const deleteAllPolygons = useCallback(() => {
        mapData.forEach((feature) => {
            if (feature.getProperty("irrigatorId") === irrigator.id) mapData.remove(feature);
        });
        syncMapDataToGeoJson(mapData);
    }, [mapData, irrigator.id, syncMapDataToGeoJson]);

    const toolbarItems = useMemo(
        () => [
            { id: "draw-polygon", icon: "icon-edit", label: "Draw polygon", active: drawingMode === "polygon", onClick: () => setDrawingMode(drawingMode === "polygon" ? null : "polygon") },
            { id: "draw-circle", icon: "icon-edit", label: "Draw circle", active: drawingMode === "circle", onClick: () => setDrawingMode(drawingMode === "circle" ? null : "circle") },
            { id: "delete-selected-polygons", icon: "icon-trash", label: "Delete selected", separator: true, onClick: deleteSelectedPolygons },
            { id: "delete-all-polygons", icon: "icon-trash", label: "Delete all", onClick: deleteAllPolygons },
        ],
        [drawingMode, setDrawingMode, deleteSelectedPolygons, deleteAllPolygons]
    );

    useEffect(() => {
        if (onChange && geoJson) {
            onChange(geoJson.features);
        }
    }, [onChange, geoJson]);

    if (!hasDrawnProductiveBlocks && !isIrrigatorDrawn) return null;

    if (isReadOnlyMode && !isIrrigatorDrawn) return null;

    return (
        <Panel title="Irrigation map" waiting={mapLoading} info={toggleDrawing ? "Draw the area that is irrigated by this irrigation system." : undefined} className="u-mt-md" midBlue>
            <PanelBody>
                {mapLoading && (
                    <div className="Tile-body-message">
                        <Spinner dark />
                        <p className="lead u-text-lg u-mt-md">Loading map...</p>
                    </div>
                )}
                {!mapLoading && mapError && (
                    <div className="Tile-body-message u-textError">
                        <i className="icon icon--md icon-alert" />
                        <p className="lead">{mapError}</p>
                    </div>
                )}
                {geoJson && !showMap && (
                    <div className="Tile-body-message u-mt-md u-mb-md">
                        <Button className="IconLink--search Button Button--secondary u-mt-md" id="show-map-button" onClick={() => setToggleDrawing((prevState) => !prevState)}>
                            Draw irrigation system on farm map
                        </Button>
                    </div>
                )}
                <div className={`irrigation_map ${showMap ? "" : "u-hidden"}`}>
                    <div className={`map ${showMap ? "" : "u-hidden"}`} ref={setMapRef} />
                </div>
                {showMap && (
                    <>
                        <MapToolbar map={map} mapId={mapId} items={toolbarItems} />
                        <BlockLabelsLayerToggle mapData={mapData} mapId={mapId} blocks={analysis.blocks} geoJson={geoJson} />
                        <MapZoomButton mapData={mapData} mapId={mapId} where={(f) => f.properties.irrigatorId === irrigator.id} padding={100} />
                        <MapErrorLabels map={map} geoJson={geoJson} />
                        <TestGeoJsonLoader mapData={mapData} onFeatureAdded={validateFeature} />
                    </>
                )}
            </PanelBody>
        </Panel>
    );
}

function useAnalysisGeoJson(analysis) {
    const [geoJson, setGeoJson] = useState();
    const initialisedRef = useRef(false);

    const syncMapDataToGeoJson = useCallback((mapData) => mapData.toGeoJson((geoJson) => setGeoJson(geoJson)), []);

    useEffect(() => {
        if (!geoJson || (!initialisedRef.current && analysis.features?.length > 0)) {
            const analysisGeoJson = { type: "FeatureCollection", features: [] };
            if (analysis.features && analysis.features.length > 0) {
                analysisGeoJson.features = analysis.features.reduce((results, feature) => {
                    // Flatten and hide unassigned land.
                    const flattenedFeatures = geoJsonUtils.flatten(feature, ["UnassignedLand"]);
                    return [...results, ...flattenedFeatures];
                }, []);
            }
            setGeoJson(analysisGeoJson);
            initialisedRef.current = analysis.features?.length > 0;
        }
    }, [analysis.features, geoJson]);

    return [geoJson, syncMapDataToGeoJson];
}

function useMapData(map, geoJson) {
    const google = useGoogleApi();
    const [mapData, setMapData] = useState();

    useEffect(() => {
        if (google && map && geoJson && !mapData) {
            const data = new google.maps.Data({ map });
            data.addGeoJson(geoJson);
            setMapData(data);
        }
    }, [google, map, geoJson, mapData]);

    return mapData;
}

function useMapStyle(mapData, irrigatorId) {
    const [runOnce, setRunOnce] = useState(false);

    useEffect(() => {
        if (mapData && irrigatorId && !runOnce) {
            mapData.setStyle((feature) => {
                const hovered = feature.getProperty("hovered");
                const selected = feature.getProperty("selected");
                const visible = feature.getProperty("visible");
                const error = feature.getProperty("error");
                const layer = feature.getProperty("layer") || "Irrigator";
                const slope = feature.getProperty("slope");
                const layerColor = geoJsonUtils.getMapLayerColor(layer, slope);

                const isCurrentIrrigator = feature.getProperty("irrigatorId") === irrigatorId;

                let zIndex = 0;
                if (hovered || selected) {
                    zIndex = 3;
                } else if (isCurrentIrrigator) {
                    zIndex = 2;
                } else if (layer === "Block") {
                    zIndex = 1;
                }

                return {
                    strokeColor: error ? "red" : layerColor,
                    strokeWeight: hovered || error ? 4 : 3,
                    fillColor: isCurrentIrrigator ? layerColor : "white",
                    fillOpacity: 0.6,
                    editable: selected,
                    zIndex,
                    visible,
                };
            });
            setRunOnce(true);
        }
    }, [mapData, irrigatorId, runOnce]);
}

// Returns the distance in metres between two google.maps.LatLng points using the haversine formula.
function haversineMetres(a, b) {
    const R = 6371000;
    const lat1 = (a.lat() * Math.PI) / 180;
    const lat2 = (b.lat() * Math.PI) / 180;
    const dLat = lat2 - lat1;
    const dLng = ((b.lng() - a.lng()) * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// Returns true if the pixel distance between two google.maps.LatLng points is within threshold pixels.
function isNearPixels(map, a, b, thresholdPx = 10) {
    const proj = map.getProjection();
    if (!proj) return false;
    const pa = proj.fromLatLngToPoint(a);
    const pb = proj.fromLatLngToPoint(b);
    const scale = 2 ** map.getZoom();
    const dx = (pa.x - pb.x) * scale;
    const dy = (pa.y - pb.y) * scale;
    return Math.sqrt(dx * dx + dy * dy) < thresholdPx;
}

function useCustomDrawing(map, mapData, irrigator, onMapDataChange, drawingMode, setDrawingMode) {
    const google = useGoogleApi();

    useEffect(() => {
        if (!google || !map || !mapData || !irrigator || !drawingMode) return;

        const layerColor = geoJsonUtils.getMapLayerColor("Irrigator");
        const previewStyle = { strokeColor: layerColor, strokeWeight: 4, fillColor: layerColor, fillOpacity: 0.6 };
        map.setOptions({ draggable: false });
        map.getDiv().style.cursor = "crosshair";

        let circleCenter = null;
        let previewCircle = null;
        let isDraggingCircle = false;
        let polygonVertices = [];
        let previewPolyline = null;
        let firstVertexMarker = null;

        const addCircleFeature = (center, radiusMetres) => {
            const circleGeoJsonFeature = geoJsonUtils.circle(center.lat(), center.lng(), radiusMetres);
            circleGeoJsonFeature.id = uuidv4();
            circleGeoJsonFeature.properties.layer = "Irrigator";
            circleGeoJsonFeature.properties.irrigatorId = irrigator.id;
            circleGeoJsonFeature.properties.selected = true;
            circleGeoJsonFeature.properties.visible = true;
            mapData.addGeoJson(circleGeoJsonFeature);
            onMapDataChange(mapData, circleGeoJsonFeature.id);
        };

        const addPolygonFeature = (vertices) => {
            const feature = new google.maps.Data.Feature({
                id: uuidv4(),
                properties: { layer: "Irrigator", irrigatorId: irrigator.id, selected: true, visible: true },
                geometry: new google.maps.Data.Polygon([vertices]),
            });
            mapData.add(feature);
            onMapDataChange(mapData, feature.id);
        };

        const cleanupPreview = () => {
            if (previewCircle) { previewCircle.setMap(null); previewCircle = null; }
            if (previewPolyline) { previewPolyline.setMap(null); previewPolyline = null; }
            if (firstVertexMarker) { firstVertexMarker.setMap(null); firstVertexMarker = null; }
            circleCenter = null;
            isDraggingCircle = false;
            polygonVertices = [];
        };

        const updateFirstVertexMarker = () => {
            if (polygonVertices.length === 1) {
                firstVertexMarker = new google.maps.Circle({ center: polygonVertices[0], radius: 0, map, strokeColor: "#ffffff", strokeWeight: 2, fillColor: layerColor, fillOpacity: 1, zIndex: 999 });
                updateFirstVertexMarkerRadius();
            }
        };

        const updateFirstVertexMarkerRadius = () => {
            if (!firstVertexMarker) return;
            // Scale the marker to a fixed pixel size regardless of zoom.
            const metersPerPx = 156543.03392 * Math.cos(polygonVertices[0].lat() * Math.PI / 180) / Math.pow(2, map.getZoom());
            firstVertexMarker.setRadius(metersPerPx * 8);
        };

        // Google Maps events can be laggy / miss clicks on Data layer features, so we use
        // DOM listeners on the map div and convert pixel → LatLng via an OverlayView.
        const overlay = new google.maps.OverlayView();
        overlay.onAdd = () => {};
        overlay.draw = () => {};
        overlay.onRemove = () => {};
        overlay.setMap(map);

        const mapDiv = map.getDiv();

        const pixelToLatLng = (e) => {
            const proj = overlay.getProjection();
            if (!proj) return null;
            const rect = mapDiv.getBoundingClientRect();
            const point = new google.maps.Point(e.clientX - rect.left, e.clientY - rect.top);
            return proj.fromContainerPixelToLatLng(point);
        };

        // --- Circle mode: click-and-drag ---
        const handleCircleMouseDown = (e) => {
            const latLng = pixelToLatLng(e);
            if (!latLng) return;
            circleCenter = latLng;
            isDraggingCircle = true;
            previewCircle = new google.maps.Circle({ center: latLng, radius: 1, map, ...previewStyle });
        };

        const handleCircleMouseMove = (e) => {
            if (!isDraggingCircle || !circleCenter || !previewCircle) return;
            const latLng = pixelToLatLng(e);
            if (latLng) previewCircle.setRadius(haversineMetres(circleCenter, latLng));
        };

        const handleCircleMouseUp = (e) => {
            if (!isDraggingCircle || !circleCenter) return;
            const latLng = pixelToLatLng(e);
            const center = circleCenter;
            const radius = latLng ? haversineMetres(center, latLng) : 0;
            cleanupPreview();
            if (radius > 1) {
                addCircleFeature(center, radius);
            }
            setDrawingMode(null);
        };

        // --- Polygon mode: click to add vertices, click near first or dblclick to close ---
        let clickTimeout = null;

        const addVertex = (latLng) => {
            polygonVertices = [...polygonVertices, latLng];
            if (!previewPolyline) {
                previewPolyline = new google.maps.Polyline({ path: polygonVertices, map, strokeColor: layerColor, strokeWeight: 3 });
            } else {
                previewPolyline.setPath(polygonVertices);
            }
            updateFirstVertexMarker();
        };

        const handlePolygonClick = (e) => {
            if (clickTimeout) { clearTimeout(clickTimeout); clickTimeout = null; return; }
            clickTimeout = setTimeout(() => {
                clickTimeout = null;
                const latLng = pixelToLatLng(e);
                if (!latLng) return;
                if (polygonVertices.length >= 3 && isNearPixels(map, latLng, polygonVertices[0], 15)) {
                    const vertices = [...polygonVertices];
                    cleanupPreview();
                    addPolygonFeature(vertices);
                    setDrawingMode(null);
                    return;
                }
                addVertex(latLng);
            }, 250);
        };

        const handlePolygonDblClick = (e) => {
            if (clickTimeout) { clearTimeout(clickTimeout); clickTimeout = null; }
            if (polygonVertices.length < 2) return;
            const latLng = pixelToLatLng(e);
            if (!latLng) return;
            const vertices = [...polygonVertices, latLng];
            cleanupPreview();
            addPolygonFeature(vertices);
            setDrawingMode(null);
        };

        const handlePolygonMouseMove = (e) => {
            const latLng = pixelToLatLng(e);
            if (!latLng) return;
            if (polygonVertices.length > 0 && previewPolyline) {
                previewPolyline.setPath([...polygonVertices, latLng]);
            }
            if (firstVertexMarker && polygonVertices.length >= 3) {
                const near = isNearPixels(map, latLng, polygonVertices[0], 15);
                firstVertexMarker.setOptions({ fillColor: near ? "#ffffff" : layerColor, strokeColor: near ? layerColor : "#ffffff" });
                mapDiv.style.cursor = near ? "pointer" : "crosshair";
            }
        };

        // Event listeners — all DOM-based for responsiveness
        if (drawingMode === "circle") {
            mapDiv.addEventListener("mousedown", handleCircleMouseDown);
            mapDiv.addEventListener("mousemove", handleCircleMouseMove);
            mapDiv.addEventListener("mouseup", handleCircleMouseUp);
        }

        if (drawingMode === "polygon") {
            mapDiv.addEventListener("click", handlePolygonClick);
            mapDiv.addEventListener("dblclick", handlePolygonDblClick);
            mapDiv.addEventListener("mousemove", handlePolygonMouseMove);
        }

        return () => {
            map.setOptions({ draggable: true });
            mapDiv.style.cursor = "";
            cleanupPreview();
            overlay.setMap(null);
            mapDiv.removeEventListener("mousedown", handleCircleMouseDown);
            mapDiv.removeEventListener("mousemove", handleCircleMouseMove);
            mapDiv.removeEventListener("mouseup", handleCircleMouseUp);
            if (clickTimeout) clearTimeout(clickTimeout);
            mapDiv.removeEventListener("click", handlePolygonClick);
            mapDiv.removeEventListener("dblclick", handlePolygonDblClick);
            mapDiv.removeEventListener("mousemove", handlePolygonMouseMove);
        };
    }, [google, map, mapData, irrigator, onMapDataChange, drawingMode, setDrawingMode]);
}

function useValidateOnLoad(mapData, irrigatorId, validateFeature) {
    const [runOnce, setRunOnce] = useState(false);

    useEffect(() => {
        if (mapData && irrigatorId && validateFeature && !runOnce) {
            mapData.forEach((mapFeature) => {
                if (mapFeature.getProperty("irrigatorId") === irrigatorId) {
                    validateFeature(mapData, mapFeature.getId());
                }
            });
            setRunOnce(true);
        }
    }, [mapData, irrigatorId, validateFeature, runOnce]);
}

function useOnPolygonHover(mapData, irrigatorId, drawingMode) {
    const [runOnce, setRunOnce] = useState(false);
    const drawingModeRef = useRef(drawingMode);
    drawingModeRef.current = drawingMode;

    useEffect(() => {
        if (mapData && irrigatorId && !runOnce) {
            // Add 'mouseover' event for hover styling.
            mapData.addListener("mouseover", ({ feature }) => {
                if (drawingModeRef.current) return;
                if (feature.getProperty("irrigatorId") === irrigatorId) {
                    feature.setProperty("hovered", true);
                }
            });

            // Add 'mouseout' event for un-hover styling.
            mapData.addListener("mouseout", ({ feature }) => {
                if (drawingModeRef.current) return;
                if (feature.getProperty("irrigatorId") === irrigatorId) {
                    feature.setProperty("hovered", false);
                }
            });

            setRunOnce(true);
        }
    }, [mapData, irrigatorId, runOnce]);
}

function useOnPolygonClick(mapData, irrigatorId, drawingMode) {
    const drawingModeRef = useRef(drawingMode);
    drawingModeRef.current = drawingMode;

    const [runOnce, setRunOnce] = useState(false);

    useEffect(() => {
        if (mapData && irrigatorId && !runOnce) {
            mapData.addListener("click", ({ feature }) => {
                if (drawingModeRef.current) return;
                if (feature.getProperty("irrigatorId") === irrigatorId) {
                    feature.setProperty("selected", !feature.getProperty("selected"));
                }
            });
            setRunOnce(true);
        }
    }, [mapData, irrigatorId, runOnce]);
}

function useOnPolygonAdd(mapData, onMapDataChange) {
    const [runOnce, setRunOnce] = useState(false);

    useEffect(() => {
        if (mapData && onMapDataChange && !runOnce) {
            mapData.addListener("addfeature", ({ feature }) => {
                feature.toGeoJson((geoJsonFeature) => {
                    const drawnArea = geoJsonUtils.area(geoJsonFeature);
                    feature.setProperty("drawnArea", drawnArea);
                    onMapDataChange(mapData, geoJsonFeature.id);

                    // De-select all other features.
                    mapData.forEach((mapFeature) => {
                        if (mapFeature.getId() !== feature.getId()) {
                            mapFeature.setProperty("selected", false);
                        }
                    });
                });
            });
            setRunOnce(true);
        }
    }, [mapData, onMapDataChange, runOnce]);
}

function useOnPolygonEdit(mapData, onMapDataChange) {
    const [runOnce, setRunOnce] = useState(false);

    useEffect(() => {
        if (mapData && onMapDataChange && !runOnce) {
            mapData.addListener("setgeometry", ({ feature }) => {
                feature.toGeoJson((geoJsonFeature) => {
                    const drawnArea = geoJsonUtils.area(geoJsonFeature);
                    feature.setProperty("drawnArea", drawnArea);
                    onMapDataChange(mapData, geoJsonFeature.id);
                });
            });
            setRunOnce(true);
        }
    }, [mapData, onMapDataChange, runOnce]);
}
